import { gql, useMutation } from '@apollo/client';
import { Button, VStack } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';

import { TopLevelComment_CommentFragment } from './graphql-types/TopLevelComment_CommentFragment';
import {
  TopLevelComment_createComment,
  TopLevelComment_createCommentVariables,
} from './graphql-types/TopLevelComment_createComment';

import { Comment } from './components/Comment';
import {
  CreateReply,
  CreateReply_CommentFragmentAST,
  CreateReply_UserFragmentAST,
} from './components/CreateReply';
import { useReplyActions } from './hooks/useReplyActions';

const TOP_LEVEL_COMMENT_COMMENT_FRAGMENT = gql`
  fragment TopLevelComment_CommentFragment on Comment {
    _id
    ...Comment_CommentFragment
    ...useReplyActions_CommentFragment
    comments {
      _id
      ...Comment_CommentFragment
    }
    post {
      _id
      currentUserCanRespondToPost
    }
    ...CreateReply_CommentFragment
  }
  ${useReplyActions.commentFragment}
  ${Comment.commentFragment}
  ${CreateReply_CommentFragmentAST}
  ${CreateReply_UserFragmentAST}
`;

const CREATE_REPLY = gql`
  mutation TopLevelComment_createComment($input: CreateCommentInput_V2!) {
    createCommentV2(input: $input) {
      data {
        _id
        comment {
          _id
        }
        ...TopLevelComment_CommentFragment
      }
    }
  }
  ${TOP_LEVEL_COMMENT_COMMENT_FRAGMENT}
  ${RawDraftContentStateFragmentAST}
`;

const EXPAND_INCREMENT = 3;

export const TopLevelComment: React.FC<{
  comment: TopLevelComment_CommentFragment;
}> & {
  commentFragment: DocumentNode;
} = ({ comment }) => {
  const {
    replyMentionedUser,
    mentionAuthorAndFocusAndScrollToRef,
    ref,
    startIndex,
    numHidden,
    expand,
    expandOrMention,
    clearReplyMentionedUser,
  } = useReplyActions({
    comment,
  });

  const [createReply, { loading }] = useMutation<
    TopLevelComment_createComment,
    TopLevelComment_createCommentVariables
  >(CREATE_REPLY);

  const createReplyComment = React.useCallback(
    async (input: TopLevelComment_createCommentVariables['input']) => {
      const result = await createReply({
        variables: {
          input,
        },
        update(cache, { data }) {
          if (!data?.createCommentV2?.data?.comment) {
            return;
          }

          cache.modify({
            //@ts-ignore
            id: cache.identify(data.createCommentV2.data.comment),
            fields: {
              comments(existingItems, { toReference }) {
                //@ts-ignore
                const cacheItemRef = toReference(data.createCommentV2.data);

                const newItems = [...existingItems, cacheItemRef];
                return newItems;
              },
            },
          });
        },
      });
      clearReplyMentionedUser();
      return result.data?.createCommentV2?.data?._id;
    },
    [createReply, clearReplyMentionedUser]
  );

  return (
    <Comment
      comment={comment}
      onClickReply={() => mentionAuthorAndFocusAndScrollToRef(comment)}
      onClickReplyCount={() => expandOrMention(comment)}
    >
      {numHidden > 0 && (
        <Button
          colorScheme="black"
          variant="link"
          onClick={() => expand(EXPAND_INCREMENT)}
          mb="2"
        >
          {numHidden > EXPAND_INCREMENT
            ? `Show ${EXPAND_INCREMENT} More Replies`
            : `Show ${numHidden} More Repl${numHidden === 1 ? 'y' : 'ies'}`}
        </Button>
      )}
      <VStack mt="1" spacing="2" align="start">
        {comment.comments.slice(startIndex).map((nestedReplyComment) => (
          <Comment
            key={nestedReplyComment._id}
            comment={nestedReplyComment}
            onClickReply={() =>
              mentionAuthorAndFocusAndScrollToRef(nestedReplyComment)
            }
          />
        ))}
        {!!comment.post?.currentUserCanRespondToPost && replyMentionedUser && (
          <CreateReply
            comment={comment}
            mentionedUser={replyMentionedUser}
            createReplyComment={createReplyComment}
            ref={ref}
            onClickCancel={clearReplyMentionedUser}
            isSubmitting={loading}
          />
        )}
      </VStack>
    </Comment>
  );
};

TopLevelComment.commentFragment = TOP_LEVEL_COMMENT_COMMENT_FRAGMENT;
